import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { FormsModule } from '@angular/forms';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Graph } from '../../models/graph';
import { Observable, Subscription } from 'rxjs';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ChartModule, UIChart } from '../../primeng/chart/chart.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Constants } from '../../utilities/constants';

@Component({
  selector: 'app-signal',
  imports: [
    FormsModule,
    PanelModule,
    ProgressSpinnerModule,
    ChartModule,
    ButtonModule,
    SelectButtonModule,
    TranslatePipe
],
  templateUrl: './signal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './signal.component.css'
})
export class SignalComponent implements OnInit, OnDestroy {
  private _segnale: Graph[][] = [];
  private _udm: string = '';

  private _xMin: number = 0;
  private _xMax: number = 0;

  private zoomCommandSubscription: Subscription | undefined;
  private zoomLevelSubscription: Subscription | undefined;

  @Input() chartHeight: string = '25vh';
  // Associa il valore dell'input alla variabile CSS custom sull'host
  @HostBinding('style.--chart-height') get height() {
    return this.chartHeight;
  }

  @Input() titolo: string = 'primeng.nullLabel';
  @Input() set udm(value: string) {
    this.options.scales.y.title.text = value;
    this._udm = value;
  }
  get udm(): string {
    return this._udm;
  }

  @Input() set segnale(value: Graph[][]) {
    this._segnale = value;

    this.data.datasets = [];
    this.initialZoom = [];
    let i: number = 0;
    this._segnale.forEach(v => {
      if (v.length > 0) {
        this.data.datasets.push({
          data: v,
          label: this.labels.length > i ? this.labels[i] : "Signal " + i,
          borderWidth: 1,         // Linea sottile per evitare l'effetto blocco
          fill: false,
          parsing: false,
        })
      }
      i = i + 1;
    })
  }
  get segnale(): Graph[][] {
    return this._segnale;
  }

  @Input() set xMax(value: number) {
    this.options.scales.x.max = value;
    this._xMax = value;
  }
  get xMax(): number {
    return this._xMax;
  }

  @Input() set xMin(value: number) {
    this.options.scales.x.min = value;
    this._xMin = value;
  }
  get xMin(): number {
    return this._xMin;
  }

  @Input() loading: boolean = false;

  @ViewChild('chart') myChartRef: UIChart | undefined;

  zoomFromInput: boolean = false;
  @Output() onZoomChanged = new EventEmitter<any>();
  @Input() zoomLevel: Observable<any> | undefined;

  zoomToOutput: boolean = false;
  @Output() onZoomCommand = new EventEmitter<number>();
  @Input() zoomCommand: Observable<number> | undefined;

  data: any;
  options: any;
  plugins: any[] = [];

  initialZoom: {id: string, value: number}[] = [];

  modeOptions:  any[] = [
    { icon: 'pi pi-search', value: 1 },
    { icon: 'pi pi-arrows-alt', value: 2 }
  ];
  mode: number = 2;

  private _useLegend: boolean = false;
  @Input() set useLegend(value: boolean) {
    this.options.plugins.legend.display = value;
    this._useLegend = value;
  }
  get useLegend(): boolean {
    return this._useLegend;
  }

  @Input() labels: string [] = [];

  constructor() {
    this.plugins = [zoomPlugin];

    this.data = {
      datasets: []
    }

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disattiva le animazioni per gestire grandi moli di dati
      indexAxis: 'x', // 1. FONDAMENTALE per la decimazione con parsing:false
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'second',
            tooltipFormat: 'HH:mm:ss.SSS', // Mostra anche i millisecondi nel tooltip
            displayFormats: {
              minute: 'HH:mm:ss',
              second: 'HH:mm:ss'
            }
          },
          ticks: {
            source: 'auto'
          }
        },
        y: {
          type: 'linear',
          title: {
            display: true,
            text: ''
          },
          ticks: {
            precision: 0 
          }
        }
      },
      plugins: {
        // 1. Attiva la decimazione dei dati
        decimation: {
          enabled: true,
          algorithm: 'lttb', // Preserva i picchi (ottimo per ECG)
          samples: 1000,     // Numero di punti massimi da mostrare nell'area visibile
        },
        legend: {
          display: false
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'xy',
          },
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: false,
            },
            drag: {
              enabled: false,
              borderWidth: 4
            },
            mode: 'x',
            scaleMode: 'xy',
            onZoomComplete: (mychart: any) => {
              if (this.zoomFromInput) {
                this.zoomFromInput = false;
              } else {
                const scaleBounds = mychart.chart.getZoomedScaleBounds();
                const scaleX: { min: number | undefined, max: number | undefined } = scaleBounds['x'];
                if (scaleX.min === undefined || scaleX.max === undefined) return;

                this.zoomToOutput = true;
                this.onZoomChanged.emit(scaleX)
              }
            }
          }
        }
      }
    };
  }

  ngOnInit(): void {
    this.zoomCommandSubscription = this.zoomCommand?.subscribe((event: number) => {
      if (this.zoomToOutput) {
        this.zoomToOutput = false;
        return;
      }

      this.zoomFromInput = true;
      switch (event) {
        case Constants.ZOOM_IN:
          this.onZoomIn();
          break;

        case Constants.ZOOM_OUT:
          this.onZoomOut();
          break;

        case Constants.ZOOM_RST:
          this.onZoomReset();
          break;

        default:
          this.zoomFromInput = false;
          break;
      }
    });

    this.zoomLevelSubscription = this.zoomLevel?.subscribe((scaleX: any) => {
      if (this.zoomToOutput) {
        this.zoomToOutput = false;
        return;
      }

      this.zoomFromInput = true;
      this.myChartRef?.chart.zoomScale('x', scaleX);
    });
  }

  ngOnDestroy(): void {
    this.zoomCommandSubscription?.unsubscribe();
    this.zoomLevelSubscription?.unsubscribe();
  }

  onModeSelection(): void {
    const useZoom: boolean = this.mode === 1;
    const usePan: boolean = this.mode === 2;

    this.options.plugins.zoom.pan.enabled = usePan;
    this.options.plugins.zoom.zoom.wheel.enabled = useZoom;
    this.options.plugins.zoom.zoom.pinch.enabled = useZoom;
    this.options.plugins.zoom.zoom.drag.enabled = useZoom;

    this.myChartRef?.chart.update();
  }

  onZoomIn() {
    if (this.zoomFromInput) {
      this.zoomFromInput = false;
    } else {
      this.zoomToOutput = true;
      this.onZoomCommand.emit(Constants.ZOOM_IN);
    }

    this.myChartRef?.chart.zoom(1.2);
  }

  onZoomOut() {
    if (this.zoomFromInput) {
      this.zoomFromInput = false;
    } else {
      this.zoomToOutput = true;
      this.onZoomCommand.emit(Constants.ZOOM_OUT);
    }

    this.myChartRef?.chart.zoom(0.8);
  }

  onZoomReset() {
    if (this.zoomFromInput) {
      this.zoomFromInput = false;
    } else {
      this.zoomToOutput = true;
      this.onZoomCommand.emit(Constants.ZOOM_RST);
    }

    this.myChartRef?.chart.resetZoom();
  }
}
